import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Pause from '@mui/icons-material/Pause';
import VolumeUp from '@mui/icons-material/VolumeUp';
import VolumeOff from '@mui/icons-material/VolumeOff';
import Replay10 from '@mui/icons-material/Replay10';
import Forward10 from '@mui/icons-material/Forward10';
import { getUnsupportedFormatMessage } from '../../utils/audioUtils';

function formatTime(time: number): string {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

interface AudioPlayerProps {
  audioUrl?: string;
  duration?: string;
  isLoadingAudio?: boolean;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, isLoadingAudio }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [audioError, setAudioError] = useState<string>('');

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Check if this is a WMA file upfront
    const fileName = audioUrl ? audioUrl.split('/').pop() || '' : '';
    if (fileName.toLowerCase().endsWith('.wma')) {
      setAudioError(getUnsupportedFormatMessage(fileName));
      return;
    }

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setTotalDuration(audio.duration);
    const handleLoadStart = () => {
      setIsLoading(true);
      setAudioError(''); // Clear any previous errors
    };
    const handleCanPlay = () => setIsLoading(false);
    const handleEnded = () => setIsPlaying(false);
    const handleError = () => {
      setIsLoading(false);
      
      const fileName = audioUrl ? audioUrl.split('/').pop() || '' : '';
      
      if (audio.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
        setAudioError(getUnsupportedFormatMessage(fileName));
      } else if (audio.error?.code === MediaError.MEDIA_ERR_NETWORK) {
        setAudioError('Network error while loading audio file.');
      } else if (audio.error?.code === MediaError.MEDIA_ERR_DECODE) {
        setAudioError('Error decoding audio file.');
      } else {
        setAudioError('Unable to play this audio file.');
      }
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (value: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = value;
    setCurrentTime(value);
  };

  const handleVolumeChange = (value: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = value;
    setVolume(value);
    setIsMuted(value === 0);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isMuted) {
      audio.volume = volume;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  };

  const skipTime = (seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = Math.max(0, Math.min(totalDuration, currentTime + seconds));
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };


  return (
    <Paper sx={{ p: 2 }}>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
      />

      <Stack spacing={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip title="Skip back 10 seconds">
            <IconButton onClick={() => skipTime(-10)} disabled={!audioUrl} size="small">
              <Replay10 />
            </IconButton>
          </Tooltip>

          <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
            <IconButton 
              onClick={togglePlayPause} 
              disabled={!audioUrl || isLoading}
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Skip forward 10 seconds">
            <IconButton onClick={() => skipTime(10)} disabled={!audioUrl} size="small">
              <Forward10 />
            </IconButton>
          </Tooltip>

          {/* Time display */}
          <Typography variant="body2" sx={{ minWidth: 100, textAlign: 'center' }}>
            {formatTime(currentTime)} / {formatTime(totalDuration)}
          </Typography>

          <Box display="flex" alignItems="center" gap={1} sx={{ ml: 'auto' }}>
            <IconButton onClick={toggleMute} size="small">
              {isMuted ? <VolumeOff /> : <VolumeUp />}
            </IconButton>
            <Slider
              value={isMuted ? 0 : volume}
              onChange={(_, value) => handleVolumeChange(value as number)}
              min={0}
              max={1}
              step={0.1}
              sx={{ width: 80 }}
              size="small"
            />
          </Box>
        </Box>

        <Box>
          <Slider
            value={currentTime}
            onChange={(_, value) => handleSeek(value as number)}
            min={0}
            max={totalDuration || 100}
            disabled={!audioUrl || totalDuration === 0}
            sx={{ 
              '& .MuiSlider-thumb': {
                width: 16,
                height: 16,
              }
            }}
          />
        </Box>

        {!audioUrl && !audioError && isLoadingAudio && (
          <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic' }}>
            Loading audio file...
          </Typography>
        )}

        {!audioUrl && !audioError && !isLoadingAudio && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Audio file not available for this call
          </Typography>
        )}

        {audioError && (
          <Box sx={{ p: 2, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
            <Typography variant="body2" color="error.main" gutterBottom>
              {audioError}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Supported formats: MP3, WAV, M4A, AAC, FLAC, OGG
            </Typography>
          </Box>
        )}

        {isLoading && !audioError && (
          <Typography variant="body2" color="primary">
            Loading audio...
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};